/**
 * Client-side EU AI Act image labelling helpers (Canvas only, no server storage).
 */

import labelBasicBlack from '../assets/eu-ai-labels/png/eu-ai-label-basic-black.png'
import labelBasicBlack50 from '../assets/eu-ai-labels/png/eu-ai-label-basic-black-50.png'
import labelBasicWhite from '../assets/eu-ai-labels/png/eu-ai-label-basic-white.png'
import labelBasicWhite50 from '../assets/eu-ai-labels/png/eu-ai-label-basic-white-50.png'
import labelGeneratedBlack from '../assets/eu-ai-labels/png/eu-ai-label-generated-black.png'
import labelGeneratedBlack50 from '../assets/eu-ai-labels/png/eu-ai-label-generated-black-50.png'
import labelGeneratedWhite from '../assets/eu-ai-labels/png/eu-ai-label-generated-white.png'
import labelGeneratedWhite50 from '../assets/eu-ai-labels/png/eu-ai-label-generated-white-50.png'
import labelModifiedBlack from '../assets/eu-ai-labels/png/eu-ai-label-modified-black.png'
import labelModifiedBlack50 from '../assets/eu-ai-labels/png/eu-ai-label-modified-black-50.png'
import labelModifiedWhite from '../assets/eu-ai-labels/png/eu-ai-label-modified-white.png'
import labelModifiedWhite50 from '../assets/eu-ai-labels/png/eu-ai-label-modified-white-50.png'

export const LABEL_TYPES = ['basic', 'generated', 'modified']
export const LABEL_COLORS = ['black', 'white', 'black-50', 'white-50']
export const LABEL_POSITIONS = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
export const SIZE_PRESETS = { small: 8, medium: 12, large: 18 }

const LABEL_ASSETS = {
  basic: {
    black: labelBasicBlack,
    white: labelBasicWhite,
    'black-50': labelBasicBlack50,
    'white-50': labelBasicWhite50
  },
  generated: {
    black: labelGeneratedBlack,
    white: labelGeneratedWhite,
    'black-50': labelGeneratedBlack50,
    'white-50': labelGeneratedWhite50
  },
  modified: {
    black: labelModifiedBlack,
    white: labelModifiedWhite,
    'black-50': labelModifiedBlack50,
    'white-50': labelModifiedWhite50
  }
}

const imageCache = new Map()

export function getLabelAssetUrl(type, color) {
  return LABEL_ASSETS[type]?.[color] || LABEL_ASSETS.generated.white
}

export function computeOutputSize(srcW, srcH, maxSide) {
  const longest = Math.max(srcW, srcH)
  if (maxSide && Number(maxSide) > 0 && longest > Number(maxSide)) {
    const scale = Number(maxSide) / longest
    return {
      width: Math.max(1, Math.round(srcW * scale)),
      height: Math.max(1, Math.round(srcH * scale))
    }
  }
  return { width: srcW, height: srcH }
}

export function computeLabelSize(outW, outH, sizePercent) {
  const longestOut = Math.max(outW, outH)
  const percent = Math.min(100, Math.max(1, Number(sizePercent) || 12))
  let labelW = longestOut * (percent / 100)
  const floor = Math.min(48, longestOut * 0.25)
  labelW = Math.max(labelW, floor)
  // Keep label within image bounds so 100% stays usable
  labelW = Math.min(labelW, outW)
  return labelW
}

export function computeLabelPlacement(outW, outH, labelW, labelH, position) {
  const longestOut = Math.max(outW, outH)
  const margin = longestOut * 0.02
  let x = margin
  let y = margin

  if (position === 'top-right' || position === 'bottom-right') {
    x = outW - labelW - margin
  }
  if (position === 'bottom-left' || position === 'bottom-right') {
    y = outH - labelH - margin
  }

  return {
    x: Math.max(0, Math.round(x)),
    y: Math.max(0, Math.round(y))
  }
}

function loadImage(src) {
  if (imageCache.has(src)) {
    return Promise.resolve(imageCache.get(src))
  }
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      imageCache.set(src, img)
      resolve(img)
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = src
  })
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load uploaded image'))
    }
    img.src = url
  })
}

export function outputFileName(originalName) {
  const base = (originalName || 'image').replace(/\.[^.]+$/, '')
  return `${base}-ai-labelled.png`
}

/**
 * Composite source image + EU AI label into a PNG Blob.
 * @param {HTMLImageElement|ImageBitmap} sourceImage
 * @param {object} options
 * @returns {Promise<{ blob: Blob, width: number, height: number, labelPx: number }>}
 */
export async function labelImage(sourceImage, options = {}) {
  const {
    type = 'generated',
    color = 'white',
    position = 'bottom-right',
    sizePercent = 12,
    maxSide = null
  } = options

  const srcW = sourceImage.naturalWidth || sourceImage.width
  const srcH = sourceImage.naturalHeight || sourceImage.height
  const { width: outW, height: outH } = computeOutputSize(srcW, srcH, maxSide)

  const labelImg = await loadImage(getLabelAssetUrl(type, color))
  const aspect = (labelImg.naturalHeight || labelImg.height) / (labelImg.naturalWidth || labelImg.width)
  const labelW = computeLabelSize(outW, outH, sizePercent)
  const labelH = labelW * aspect
  const { x, y } = computeLabelPlacement(outW, outH, labelW, labelH, position)

  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const ctx = canvas.getContext('2d')
  ctx.drawImage(sourceImage, 0, 0, outW, outH)
  ctx.drawImage(labelImg, x, y, labelW, labelH)

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => (result ? resolve(result) : reject(new Error('Canvas export failed'))),
      'image/png'
    )
  })

  return {
    blob,
    width: outW,
    height: outH,
    labelPx: Math.round(labelW)
  }
}

export async function labelFile(file, options = {}) {
  const sourceImage = await loadImageFromFile(file)
  const result = await labelImage(sourceImage, options)
  return {
    ...result,
    fileName: outputFileName(file.name)
  }
}

export function revokeObjectUrl(url) {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url)
  }
}
